import { Box, Icon, IconId, Typography } from '@drone-js/core';

import { HighlightText } from 'component/common/HighlightText/HighlightText';
import React from 'react';
import { selectFeatureExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { selectScreenSize } from 'store/slice/screen';
import { useSelector } from 'react-redux';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';
import { ExperimentId } from 'core/type/ExperimentId';
import { splitTextWithoutRegex } from 'core/utils/searchUtils';
import { MapTextSearchTypeIdToText, TextSearchType } from 'core/type/TextSearch.types';

export interface SuggestionItemProps {
    elementName: string;
    title: string;
    subTitle: string;
    icon: IconId;
    searchText?: string;
    onSelect: () => void;
    searchType?: TextSearchType;
}

const SuggestionItemComponent: React.FC<SuggestionItemProps> = React.memo((props) => {
    const isSearchBoxPopOverFeature = useSelector(selectFeatureExperimentsFromBootstrap).featureBehavior.SearchBoxPopOverFeature;
    const isSearchKeywordHighlight = useExperimentIsB(ExperimentId.ACT_4576);
    const screenSize = useSelector(selectScreenSize);

    const getMatchedTypography = (text: string) => {
        if (!text) return '';
        if (!props.searchText) return text;

        const sanitizedSearchText = props.searchText.trimStart().trimEnd();
        const parts = splitTextWithoutRegex(text, sanitizedSearchText);

        return parts.map((part, index) => {
            return part.toLowerCase() === sanitizedSearchText?.toLowerCase() ? (
                <HighlightText key={index}>{part}</HighlightText>
            ) : (
                <React.Fragment key={index}>{part}</React.Fragment>
            );
        });
    };
    const getType: string = MapTextSearchTypeIdToText[props.searchType as TextSearchType];

    return (
        <Box
            mx={screenSize.mobile || isSearchBoxPopOverFeature ? '0' : '4'}
            as="button"
            p="8"
            px="0"
            display="flex"
            borderRadius="8"
            interactive="product.primary"
            flexDirection="row"
            onClick={props.onSelect}
            width="100%"
            aria-label={props.title}
            data-testid={props.elementName}
            data-element-name={props.elementName}
            data-element-value={props.title}
            data-result-type={getType}
            role="listitem"
        >
            <Box width="48px" ps="4">
                <Box width="48px" height="48px" backgroundColor="base.background" borderRadius="8" display="flex" justifyContent="center" alignItems="center">
                    <Icon id={props.icon} size="20" />
                </Box>
            </Box>
            <Box ps="12" display="flex" flexDirection="column" alignItems="flex-start" textAlign="left" gap="4">
                <Typography variant="body-s-strong" lineClamp={2}>
                    {isSearchBoxPopOverFeature && isSearchKeywordHighlight ? getMatchedTypography(props.title) : props.title}
                </Typography>

                {props.subTitle && (
                    <Typography variant="body-s" textColor="base.subtle">
                        {props.subTitle}
                    </Typography>
                )}
            </Box>
        </Box>
    );
});

SuggestionItemComponent.displayName = 'SuggestionItemComponent';
export default SuggestionItemComponent;
